import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { GiftIcon, TimerIcon, GitBranchIcon, ShieldCheckIcon, CheckIcon } from 'lucide-react';
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const cardVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function BonusSection() {
  return <section className="px-4 py-20 md:py-28 bg-midnight-900/50" data-id="element-32">
      <div className="max-w-3xl mx-auto" data-id="element-33">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} variants={sectionVariants} className="text-center mb-6" data-id="element-34">
          <div className="inline-flex items-center gap-2 bg-rescue-gold/10 border border-rescue-gold/25 rounded-full px-4 py-2 mb-6" data-id="element-35">
            <GiftIcon className="w-4 h-4 text-rescue-gold" aria-hidden="true" data-id="element-36" />
            <span className="font-body text-sm font-semibold text-rescue-gold uppercase tracking-wide" data-id="element-37">
              A $27 Value — Yours Free Today
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-38">
            But Wait — Your Order Also Includes 3 Free Bonuses
          </h2>
        </motion.div>

        <motion.p initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} variants={sectionVariants} className="font-body text-base md:text-lg text-gray-400 text-center max-w-2xl mx-auto mb-16 leading-relaxed" data-id="element-39">
          The $0 Tax Rescue Guide will get you filed. But these three bonuses
          make sure you get there fast, confident, and without a single "now
          what?" moment.
        </motion.p>

        {/* Bonus Cards */}
        <motion.div variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="space-y-12" data-id="element-40">
          {/* BONUS #1 */}
          <motion.div variants={cardItem} className="bg-midnight-800 border border-midnight-700/60 rounded-xl overflow-hidden" data-id="element-41">
            <div className="md:flex" data-id="element-42">
              <div className="md:w-2/5 flex-shrink-0" data-id="element-43">
                <img src="https://cdn.magicpatterns.com/uploads/sux21DPJvNrYqb9Sk3RzXh/10-Minute-Tax-Triage-Quick-Start_Image.png" alt="10-Minute Tax Triage Quick-Start shown on a tablet screen" className="w-full h-full object-cover" data-id="element-44" />
              </div>
              <div className="p-6 md:p-8 md:w-3/5" data-id="element-45">
                <div className="flex items-center gap-3 mb-4" data-id="element-46">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-47">
                    <GiftIcon className="w-4 h-4 text-rescue-gold" aria-hidden="true" data-id="element-48" />
                  </div>
                  <span className="font-body text-xs font-bold uppercase tracking-widest text-rescue-gold" data-id="element-49">
                    Free Bonus #1
                  </span>
                </div>

                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-1" data-id="element-50">
                  The 10-Minute Tax Triage Quick-Start
                </h3>
                <p className="font-body text-sm text-gray-500 italic mb-3" data-id="element-51">
                  "Start Here Tonight"
                </p>
                <div className="inline-flex items-center gap-1.5 bg-rescue-green/10 border border-rescue-green/25 rounded-full px-3 py-1 mb-5" data-id="element-52">
                  <span className="font-body text-xs font-semibold text-rescue-green" data-id="element-53">
                    Value: $9 — Yours FREE
                  </span>
                </div>

                <p className="font-body text-gray-400 leading-relaxed mb-4" data-id="element-54">
                  Before you open anything else — open this.
                </p>
                <p className="font-body text-gray-400 leading-relaxed mb-4" data-id="element-55">
                  In 10 minutes flat you will know your exact filing path, the
                  only documents you actually need tonight, and the 5 red-flag
                  mistakes that cause rejections and refund delays before you
                  make them.
                </p>
                <p className="font-body text-gray-300 font-semibold leading-relaxed mb-2" data-id="element-56">
                  No more staring at the screen wondering where to begin.
                </p>
                <p className="font-body text-rescue-gold text-sm font-semibold" data-id="element-57">
                  This bonus alone will save you 45 minutes of confusion.
                </p>
              </div>
            </div>
          </motion.div>

          {/* BONUS #2 */}
          <motion.div variants={cardItem} className="bg-midnight-800 border border-midnight-700/60 rounded-xl overflow-hidden" data-id="element-58">
            <div className="md:flex" data-id="element-59">
              <div className="md:w-2/5 flex-shrink-0" data-id="element-60">
                <img src="https://cdn.magicpatterns.com/uploads/oc8yeo7k362FS5gfrfNv12/W2-1099-PanicProof-Category-Map.png" alt="W-2 + 1099 Panic-Proof Category Map shown on a tablet screen" className="w-full h-full object-cover" data-id="element-61" />
              </div>
              <div className="p-6 md:p-8 md:w-3/5" data-id="element-62">
                <div className="flex items-center gap-3 mb-4" data-id="element-63">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-64">
                    <GiftIcon className="w-4 h-4 text-rescue-gold" aria-hidden="true" data-id="element-65" />
                  </div>
                  <span className="font-body text-xs font-bold uppercase tracking-widest text-rescue-gold" data-id="element-66">
                    Free Bonus #2
                  </span>
                </div>

                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-1" data-id="element-67">
                  The W-2 + 1099 Panic-Proof Category Map
                </h3>
                <p className="font-body text-sm text-gray-500 italic mb-3" data-id="element-68">
                  "So You Don't Guess"
                </p>
                <div className="inline-flex items-center gap-1.5 bg-rescue-green/10 border border-rescue-green/25 rounded-full px-3 py-1 mb-5" data-id="element-69">
                  <span className="font-body text-xs font-semibold text-rescue-green" data-id="element-70">
                    Value: $9 — Yours FREE
                  </span>
                </div>

                <p className="font-body text-gray-400 leading-relaxed mb-4" data-id="element-71">
                  Have a day job AND side hustle income? This is the bonus that
                  was built specifically for you.
                </p>
                <p className="font-body text-gray-400 leading-relaxed mb-5" data-id="element-72">
                  The Panic-Proof Category Map shows you exactly where every
                  dollar of your W-2 and 1099 income goes — using a dead-simple
                  "3 Buckets System" that eliminates the guesswork on every
                  confusing screen.
                </p>

                <ul className="space-y-2.5 mb-5" data-id="element-73">
                  <li className="flex items-start gap-2.5" data-id="element-74">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-75" />
                    <span className="font-body text-sm text-gray-300" data-id="element-76">
                      Know exactly what to enter and where
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5" data-id="element-77">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-78" />
                    <span className="font-body text-sm text-gray-300" data-id="element-79">
                      Claim only the expenses that are safe tonight
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5" data-id="element-80">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-81" />
                    <span className="font-body text-sm text-gray-300" data-id="element-82">
                      Never freeze on the "Schedule C" screen again
                    </span>
                  </li>
                </ul>

                <p className="font-body text-rescue-gold text-sm font-semibold" data-id="element-83">
                  If your taxes are "mixed," this bonus is the difference
                  between stuck and filed.
                </p>
              </div>
            </div>
          </motion.div>

          {/* BONUS #3 */}
          <motion.div variants={cardItem} className="bg-midnight-800 border border-midnight-700/60 rounded-xl overflow-hidden" data-id="element-84">
            <div className="md:flex" data-id="element-85">
              <div className="md:w-2/5 flex-shrink-0" data-id="element-86">
                <img src="https://cdn.magicpatterns.com/uploads/kgx5RgKyzQrKfS9ZxA4t3o/Submit-With-Confidence.png" alt="Submit With Confidence — 7-Point Error Shield book cover" className="w-full h-full object-cover" data-id="element-87" />
              </div>
              <div className="p-6 md:p-8 md:w-3/5" data-id="element-88">
                <div className="flex items-center gap-3 mb-4" data-id="element-89">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-90">
                    <GiftIcon className="w-4 h-4 text-rescue-gold" aria-hidden="true" data-id="element-91" />
                  </div>
                  <span className="font-body text-xs font-bold uppercase tracking-widest text-rescue-gold" data-id="element-92">
                    Free Bonus #3
                  </span>
                </div>

                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-1" data-id="element-93">
                  Submit With Confidence — The 7-Point Error Shield
                </h3>
                <p className="font-body text-sm text-gray-500 italic mb-3" data-id="element-94">
                  "2-Minute Final Review Before You Hit Submit"
                </p>
                <div className="inline-flex items-center gap-1.5 bg-rescue-green/10 border border-rescue-green/25 rounded-full px-3 py-1 mb-5" data-id="element-95">
                  <span className="font-body text-xs font-semibold text-rescue-green" data-id="element-96">
                    Value: $9 — Yours FREE
                  </span>
                </div>

                <p className="font-body text-gray-400 leading-relaxed mb-4" data-id="element-97">
                  This is the last thing you do before you hit Submit.
                </p>
                <p className="font-body text-gray-400 leading-relaxed mb-5" data-id="element-98">
                  A quick 7-point pre-flight checklist that catches the exact
                  mistakes that cause rejected returns, delayed refunds, and
                  midnight panic — before they happen.
                </p>

                <ul className="space-y-2 mb-5" data-id="element-99">
                  <li className="flex items-center gap-2.5" data-id="element-100">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0" aria-hidden="true" data-id="element-101" />
                    <span className="font-body text-sm text-gray-300" data-id="element-102">
                      Name spellings verified
                    </span>
                  </li>
                  <li className="flex items-center gap-2.5" data-id="element-103">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0" aria-hidden="true" data-id="element-104" />
                    <span className="font-body text-sm text-gray-300" data-id="element-105">
                      SSNs verified
                    </span>
                  </li>
                  <li className="flex items-center gap-2.5" data-id="element-106">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0" aria-hidden="true" data-id="element-107" />
                    <span className="font-body text-sm text-gray-300" data-id="element-108">
                      Bank routing confirmed
                    </span>
                  </li>
                  <li className="flex items-center gap-2.5" data-id="element-109">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0" aria-hidden="true" data-id="element-110" />
                    <span className="font-body text-sm text-gray-300" data-id="element-111">
                      State return included
                    </span>
                  </li>
                  <li className="flex items-center gap-2.5" data-id="element-112">
                    <CheckIcon className="w-4 h-4 text-rescue-green flex-shrink-0" aria-hidden="true" data-id="element-113" />
                    <span className="font-body text-sm text-gray-300" data-id="element-114">
                      Confirmation screenshots saved
                    </span>
                  </li>
                </ul>

                <p className="font-body text-gray-400 text-sm leading-relaxed" data-id="element-115">
                  <span className="text-gray-300 font-semibold" data-id="element-116">Plus:</span> A
                  calm "if rejected, fix this first" recovery plan so a
                  rejection code never spirals into a panic attack.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bonus Stack Summary Table */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="mt-16 bg-midnight-800 border border-rescue-gold/20 rounded-xl overflow-hidden" data-id="element-117">
          <div className="px-6 py-4 bg-rescue-gold/5 border-b border-rescue-gold/20" data-id="element-118">
            <div className="flex items-center gap-2" data-id="element-119">
              <GiftIcon className="w-5 h-5 text-rescue-gold" aria-hidden="true" data-id="element-120" />
              <h3 className="font-heading text-lg font-bold text-white" data-id="element-121">
                What You're Getting Free
              </h3>
            </div>
          </div>

          <div className="divide-y divide-midnight-700/60" data-id="element-122">
            <div className="flex items-center justify-between px-6 py-4" data-id="element-123">
              <div className="flex items-center gap-3" data-id="element-124">
                <TimerIcon className="w-4 h-4 text-rescue-gold flex-shrink-0" aria-hidden="true" data-id="element-125" />
                <span className="font-body text-sm text-gray-300" data-id="element-126">
                  Bonus #1: 10-Minute Tax Triage Quick-Start
                </span>
              </div>
              <span className="font-body text-sm font-semibold text-gray-400 flex-shrink-0 ml-4" data-id="element-127">
                $9.00
              </span>
            </div>

            <div className="flex items-center justify-between px-6 py-4" data-id="element-128">
              <div className="flex items-center gap-3" data-id="element-129">
                <GitBranchIcon className="w-4 h-4 text-rescue-gold flex-shrink-0" aria-hidden="true" data-id="element-130" />
                <span className="font-body text-sm text-gray-300" data-id="element-131">
                  Bonus #2: W-2 + 1099 Panic-Proof Category Map
                </span>
              </div>
              <span className="font-body text-sm font-semibold text-gray-400 flex-shrink-0 ml-4" data-id="element-132">
                $9.00
              </span>
            </div>

            <div className="flex items-center justify-between px-6 py-4" data-id="element-133">
              <div className="flex items-center gap-3" data-id="element-134">
                <ShieldCheckIcon className="w-4 h-4 text-rescue-gold flex-shrink-0" aria-hidden="true" data-id="element-135" />
                <span className="font-body text-sm text-gray-300" data-id="element-136">
                  Bonus #3: Submit With Confidence (7-Point Error Shield)
                </span>
              </div>
              <span className="font-body text-sm font-semibold text-gray-400 flex-shrink-0 ml-4" data-id="element-137">
                $9.00
              </span>
            </div>

            <div className="flex items-center justify-between px-6 py-4 bg-midnight-700/30" data-id="element-138">
              <span className="font-body text-sm font-bold text-white" data-id="element-139">
                Total Bonus Value
              </span>
              <span className="font-body text-sm font-bold text-gray-400 line-through flex-shrink-0 ml-4" data-id="element-140">
                $27.00
              </span>
            </div>

            <div className="flex items-center justify-between px-6 py-4 bg-rescue-green/5" data-id="element-141">
              <span className="font-body text-sm font-bold text-white" data-id="element-142">
                Your Cost
              </span>
              <span className="font-body text-lg font-bold text-rescue-green flex-shrink-0 ml-4" data-id="element-143">
                FREE
              </span>
            </div>
          </div>
        </motion.div>

        {/* Closing Transition */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="mt-12 text-center max-w-2xl mx-auto" data-id="element-144">
          <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed mb-3" data-id="element-145">
            These three bonuses are included automatically with your $6.99
            order. No extra steps. No separate downloads. Everything lands in
            your inbox the moment you check out.
          </p>
          <p className="font-body text-base md:text-lg text-rescue-gold font-semibold" data-id="element-146">
            The guide gets you started. The bonuses make sure you finish.
          </p>
        </motion.div>
      </div>
    </section>;
}